import { UnitsSystem } from "libapi";
import { render_length, roundNumber } from "../../../shared/shared";
import { LabelSide } from "blackbox";
import { ModelRect } from "./types";
import { Rect2d } from "../../layout_utils";

export function getDeltaString(
	pDeltaX: number,
	pDeltaY: number,
	pX: number,
	pY: number,
	pUnits: UnitsSystem
): string {
	const lDisplayX = roundNumber(pX * 100),
		lDisplayY = roundNumber(pY * 100);
	return `X: ${lDisplayX}%${renderDelta(
		pDeltaX,
		pUnits
	)} Y: ${lDisplayY}%${renderDelta(pDeltaY, pUnits)}`;
}

function renderDelta(pDelta: number, pUnits: UnitsSystem): string {
	if (Math.abs(pDelta) < 1) return '';
	const lDisplay = render_length(pDelta, pUnits, true);
	return ` (${pDelta > 0 ? '+' : ''}${lDisplay})`;
}

/**
 * Gets the next label side position based on the current label side
 * in a clockwise direction (Back -> Right -> Front -> Left)
 * @param labelSide The current label side position
 * @returns The next label side position
 */
function getAdjacentLabelSide(labelSide: LabelSide): LabelSide {
  switch (labelSide) {
    case LabelSide.Back:
      return LabelSide.Right;
    case LabelSide.Front:
      return LabelSide.Left;
    case LabelSide.Left:
      return LabelSide.Back;
    case LabelSide.Right:
      return LabelSide.Front;
    default:
      return LabelSide.None;
  }
}

/**
 * Gets the canvas-ready label position and size given a box and a base label rectangle
 * @param box The 2D box to place the label on
 * @param labelRectData The base label rectangle data
 * @returns The label rectangle data with the correct position and size
 */
export const getLabelRectData = (box: Omit<ModelRect, "fill" | "stroke" | "strokeWidth">, labelRectData: Rect2d): Rect2d | undefined => {
  if (box.labelSide === LabelSide.None) return undefined;

  const labelSideWithSpin: LabelSide = !box.spin 
  // if a box doesn't have spin, the label is effectively on the opposite side
  ? getAdjacentLabelSide(getAdjacentLabelSide(box.labelSide))
  : box.labelSide;

  const labelSideWithSpinAndRotation =  box.rotated ? getAdjacentLabelSide(labelSideWithSpin) : labelSideWithSpin;

  switch(labelSideWithSpinAndRotation) {
    case LabelSide.Back:
      return {
        x: box.width - labelRectData.width - labelRectData.x,
        y: labelRectData.y,
        width: labelRectData.width,
        height: labelRectData.height,
      };
    case LabelSide.Front:
      return {
        x: labelRectData.x,
        y: box.height - labelRectData.height - labelRectData.y,
        width: labelRectData.width,
        height: labelRectData.height
      };
    case LabelSide.Left: 
      return {
        x: labelRectData.x,
        y: labelRectData.y,
        width: labelRectData.height,
        height: labelRectData.width
      }
    case LabelSide.Right:
      return {
        x: box.width - labelRectData.height - labelRectData.x,
        y: box.height - labelRectData.width - labelRectData.y,
        width: labelRectData.height,
        height: labelRectData.width
      }
  }
}